import { useMemo } from 'react'

import { AddressSearchTabData } from '~/components/component-library/AddressSearchInput/types'
import {
  filterResultsByQuery,
  groupAllResults,
  mergeResults,
  partitionResultsByInternal,
  sortInternalFirst,
  useFilteredAddresses,
} from '~/components/component-library/AddressSearchInput/utils'
import { SupportedChainId } from '~/constants/chains'
import { usePayeeSearch } from '~/hooks/compliance/usePayeeSearch'
import { useActiveOrg } from '~/hooks/org'
import { useContactSearch } from '~/hooks/useContacts'
import { useAddressLookup, useENSLookup } from '~/hooks/useENS'
import { useFarcasterSearch } from '~/hooks/useFarcaster'
import {
  useOrgRootSmartAccount,
  useSmartAccounts,
} from '~/hooks/useSmartAccounts'
import { IAddress, AddressSearchResult, VmType } from '~/types'
import { Address } from '~/utils/address'

type UseAddressSearchDataArgs = {
  query: string
  chainId?: SupportedChainId
  filteredAddresses: IAddress[]
  searchHistory: AddressSearchResult[]
  searchPlaceholder: AddressSearchResult[]
  vmTypes?: VmType[]
  includeBankAccounts?: boolean
}

export const useAddressSearchData = ({
  query,
  chainId,
  filteredAddresses,
  searchHistory,
  searchPlaceholder,
  vmTypes,
  includeBankAccounts = false,
}: UseAddressSearchDataArgs) => {
  const { org } = useActiveOrg()
  const { smartAccounts } = useSmartAccounts({
    includeAutomations: true,
  })

  const filteredAddressesSet = useMemo(
    () => new Set(filteredAddresses),
    [filteredAddresses],
  )

  // The Root account is a real internal account but must never be offered as a
  // search target (it can't transact). Drop it from the suggestion lists only —
  // pasting its address still resolves, since selection uses filteredAddressesSet.
  const { smartAccount: rootAccount, isLoading: isLoadingRoot } =
    useOrgRootSmartAccount()
  const excludeRoot = useMemo(
    () => (rows: AddressSearchResult[]) =>
      rootAccount
        ? rows.filter((row) => !Address.equal(row.address, rootAccount.address))
        : rows,
    [rootAccount],
  )

  // Bank accounts surfaced as results, keyed by liquidationAddress so selection
  // flows through the normal address path.
  const allBankResults = useMemo<AddressSearchResult[]>(() => {
    if (!includeBankAccounts) return []
    return (org?.bridge?.externalAccounts ?? [])
      .filter(
        (account) => !filteredAddressesSet.has(account.liquidationAddress),
      )
      .map((account) => ({
        address: account.liquidationAddress,
        bankAccount: {
          externalAccountId: account.externalAccountId,
          name: account.name,
          accountNumberLastFour: account.accountNumberLastFour,
        },
      }))
  }, [includeBankAccounts, org?.bridge?.externalAccounts, filteredAddressesSet])

  // Those matching the query (name / last-4 / address) once the user types.
  const bankResults = useMemo<AddressSearchResult[]>(() => {
    const normalizedQuery = query.trim().toLowerCase()
    if (!normalizedQuery) return allBankResults
    return allBankResults.filter(
      (result) =>
        result.bankAccount &&
        (result.bankAccount.name.toLowerCase().includes(normalizedQuery) ||
          result.bankAccount.accountNumberLastFour.includes(normalizedQuery) ||
          result.address.toLowerCase().includes(normalizedQuery)),
    )
  }, [allBankResults, query])

  const { results: contactResults, isLoading: isLoadingSearchOrg } =
    useContactSearch(query)
  const { results: farcasterResults, isLoading: isLoadingSearchFarcaster } =
    useFarcasterSearch(query)
  const { address: ensResolution, isLoading: isLoadingENS } = useENSLookup(
    query,
    chainId,
  )
  const { data: addressResolution, isLoading: isLoadingAddress } =
    useAddressLookup(query, chainId)

  const { results: payeeResults, isLoading: isLoadingPayee } =
    usePayeeSearch(query)

  const mergedResults = useFilteredAddresses(
    filteredAddressesSet,
    mergeResults({
      contactResults: contactResults || [],
      farcasterResults: farcasterResults || [],
      ensResults: ensResolution
        ? [{ address: ensResolution, ens: { name: query } }]
        : [],
      payeeResults: payeeResults || [],
    }),
  )

  const filterByVmTypes = useMemo(() => {
    if (!vmTypes) return (results: AddressSearchResult[]) => results
    return (results: AddressSearchResult[]) =>
      results.filter((r) =>
        Address.vmTypes(Address.format(r.address)).some((vm) =>
          vmTypes.includes(vm),
        ),
      )
  }, [vmTypes])

  const results = useMemo(
    () => excludeRoot(filterByVmTypes(sortInternalFirst(mergedResults))),
    [excludeRoot, filterByVmTypes, mergedResults],
  )

  // The org's internal accounts (including automations; Root and archived are
  // excluded by the hook) surfaced as a browsable dataset.
  const accounts = useMemo(
    () =>
      filterByVmTypes(
        smartAccounts
          .filter((account) => !filteredAddressesSet.has(account.address))
          .map((account) => ({
            address: account.address,
            smartAccountName: account.name,
          })),
      ),
    [filterByVmTypes, smartAccounts, filteredAddressesSet],
  )
  const accountMatches = useMemo(
    () => filterResultsByQuery(accounts, query),
    [accounts, query],
  )

  // Labeled contacts (the address book minus internal accounts).
  const rawFilteredPlaceholder = useFilteredAddresses(
    filteredAddressesSet,
    searchPlaceholder,
  )
  const filteredSearchPlaceholder = useMemo(
    () => excludeRoot(filterByVmTypes(rawFilteredPlaceholder)),
    [excludeRoot, filterByVmTypes, rawFilteredPlaceholder],
  )
  const contacts = useMemo(
    () =>
      filteredSearchPlaceholder.filter((result) => !result.smartAccountName),
    [filteredSearchPlaceholder],
  )
  const contactMatches = useMemo(
    () => filterResultsByQuery(contacts, query),
    [contacts, query],
  )

  // Unified recents in most-recent-first storage order. Bank recents only
  // surface for consumers that show banks at all.
  const recents = useMemo(
    () =>
      excludeRoot(
        filterByVmTypes(
          searchHistory.filter(
            (result) =>
              !filteredAddressesSet.has(result.address) &&
              (includeBankAccounts || !result.bankAccount),
          ),
        ),
      ),
    [
      excludeRoot,
      filterByVmTypes,
      searchHistory,
      filteredAddressesSet,
      includeBankAccounts,
    ],
  )

  const { internal: internalHistory, external: externalHistory } = useMemo(
    () => partitionResultsByInternal(recents),
    [recents],
  )

  const all = useMemo(
    () =>
      groupAllResults({
        searchResults: results,
        accountMatches,
        allBanks: allBankResults,
        matchingBanks: bankResults,
        query,
      }),
    [results, accountMatches, allBankResults, bankResults, query],
  )

  const tabData = useMemo<AddressSearchTabData>(
    () => ({
      recents,
      accounts: { browse: accounts, matching: accountMatches },
      banks: { browse: allBankResults, matching: bankResults },
      contacts: { browse: contacts, matching: contactMatches },
      all,
    }),
    [
      recents,
      accounts,
      accountMatches,
      allBankResults,
      bankResults,
      contacts,
      contactMatches,
      all,
    ],
  )

  const isLoading =
    isLoadingSearchOrg ||
    isLoadingSearchFarcaster ||
    isLoadingENS ||
    isLoadingAddress ||
    isLoadingPayee ||
    // ensure results never render before the Root filter is ready
    isLoadingRoot

  return {
    addressResolution,
    ensResolution,
    filteredAddressesSet,
    results,
    internalHistory,
    externalHistory,
    filteredSearchPlaceholder,
    tabData,
    isLoading,
  }
}
